import { hasFeature } from '@precert/has-feature';
import { FormSchema } from '@precert/validation';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Row, Col, FormGroup } from 'reactstrap';

import { useConfig } from '../../../../_components/config';
import { Feature } from '../../../../_components/feature';
import InputField from '../../../../_components/input-field';
import PhoneInput from '../../../../_components/inputs/phone-input';
import SelectField from '../../../../_components/select-components/select-field';
import type { WizardStepProps } from '../wizard';

export interface StepPointOfContactProps {
  index: number;
}

export interface StepPointOfContactFormData {
  pointOfContactFirstName: string;
  pointOfContactLastName: string;
  pointOfContactPhone: string;
  pointOfContactFax: string;
  pointOfContactEmail: string;
  hasAlternatePointOfContact: boolean;
  alternatePointOfContactFirstName: string;
  alternatePointOfContactLastName: string;
  alternatePointOfContactPhone: string;
  alternatePointOfContactFax: string;
  alternatePointOfContactEmail: string;
}

export const StepPointOfContactSchema = FormSchema().pick([
  'pointOfContactFirstName',
  'pointOfContactLastName',
  'pointOfContactPhone',
  'pointOfContactFax',
  'pointOfContactEmail',
  'hasAlternatePointOfContact',
  'alternatePointOfContactFirstName',
  'alternatePointOfContactLastName',
  'alternatePointOfContactPhone',
  'alternatePointOfContactFax',
  'alternatePointOfContactEmail',
]);

export function StepPointOfContact() {
  const { watch, clearErrors, resetField } = useFormContext();
  const { features } = useConfig();
  const hasAlternatePointOfContact = watch('hasAlternatePointOfContact');
  useEffect(() => {
    if (!hasAlternatePointOfContact) {
      clearErrors([
        'alternatePointOfContactFirstName',
        'alternatePointOfContactLastName',
        'alternatePointOfContactPhone',
        'alternatePointOfContactFax',
        'alternatePointOfContactEmail',
      ]);
      resetField('alternatePointOfContactFirstName');
      resetField('alternatePointOfContactLastName');
      resetField('alternatePointOfContactPhone');
      resetField('alternatePointOfContactFax');
      resetField('alternatePointOfContactEmail');
    }
  }, [hasAlternatePointOfContact, clearErrors, resetField]);
  return (
    <div className="pt-0">
      <Row className="justify-content-center">
        <Col lg={6}>
          <FormGroup>
            <InputField
              label="First Name"
              id="pointOfContactFirstName"
              name="pointOfContactFirstName"
              aria-label="First Name"
              type="text"
            />
          </FormGroup>
        </Col>

        <Col lg={6}>
          <FormGroup>
            <InputField
              required
              id="pointOfContactLastName"
              label="Last Name"
              aria-label="Last Name"
              name="pointOfContactLastName"
              type="text"
            />
          </FormGroup>
        </Col>
      </Row>
      <Row className="justify-content-start">
        <Col lg={3}>
          <FormGroup>
            <PhoneInput
              required
              aria-description="Phone must be in the format: NNN-NNN-NNNN"
              aria-label="Phone"
              label="Phone"
              name="pointOfContactPhone"
              id="pointOfContactPhone"
            />
          </FormGroup>
        </Col>
        <Col lg={3}>
          <FormGroup>
            <PhoneInput
              aria-description="Fax must be in the format: NNN-NNN-NNNN"
              aria-label="Fax"
              label="Fax"
              name="pointOfContactFax"
              id="pointOfContactFax"
            />
          </FormGroup>
        </Col>
        <Col lg={6}>
          <FormGroup>
            <InputField
              required={hasFeature('alt-poc', features)}
              aria-label="Email"
              label="Email"
              aria-description="Email should be in the format: xxx@yyy.zzz"
              name="pointOfContactEmail"
              id="pointOfContactEmail"
            />
          </FormGroup>
        </Col>
      </Row>
      <Feature id="alt-poc">
        <Row>
          <Col sm={12} md={6}>
            <FormGroup>
              <SelectField
                required
                options={[
                  {
                    label: 'No',
                    value: false,
                  },
                  {
                    label: 'Yes',
                    value: true,
                  },
                ]}
                label="Would you like to provide an alternate point of contact?"
                name="hasAlternatePointOfContact"
              />
            </FormGroup>
          </Col>
        </Row>
        {hasAlternatePointOfContact && (
          <>
            <hr aria-hidden="true" />
            <h2>Alternate Point of Contact</h2>
            <Row className="justify-content-center">
              <Col lg={6}>
                <FormGroup>
                  <InputField
                    label="First Name"
                    id="alternatePointOfContactFirstName"
                    name="alternatePointOfContactFirstName"
                    aria-label="First Name"
                    type="text"
                  />
                </FormGroup>
              </Col>

              <Col lg={6}>
                <FormGroup>
                  <InputField
                    required
                    id="alternatePointOfContactLastName"
                    label="Last Name"
                    aria-label="Last Name"
                    name="alternatePointOfContactLastName"
                    type="text"
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row className="justify-content-start">
              <Col lg={3}>
                <FormGroup>
                  <PhoneInput
                    required
                    aria-description="Phone must be in the format: NNN-NNN-NNNN"
                    aria-label="Phone"
                    label="Phone"
                    name="alternatePointOfContactPhone"
                    id="alternatePointOfContactPhone"
                  />
                </FormGroup>
              </Col>
              <Col lg={3}>
                <FormGroup>
                  <PhoneInput
                    aria-description="Fax must be in the format: NNN-NNN-NNNN"
                    aria-label="Fax"
                    label="Fax"
                    name="alternatePointOfContactFax"
                    id="alternatePointOfContactFax"
                  />
                </FormGroup>
              </Col>
              <Col lg={6}>
                <FormGroup>
                  <InputField
                    aria-label="Email"
                    required
                    label="Email"
                    aria-description="Email should be in the format: xxx@yyy.zzz"
                    name="alternatePointOfContactEmail"
                    id="alternatePointOfContactEmail"
                  />
                </FormGroup>
              </Col>
            </Row>
          </>
        )}
      </Feature>
    </div>
  );
}

export const stepPointOfContact: WizardStepProps = {
  title: 'Point of Contact',
  schema: StepPointOfContactSchema,
  element: <StepPointOfContact />,
  path: 'point-of-contact',
};
